/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionsEventActionNames,
  ActionsEventNavigationPathSegment,
  PageToPageNavigationPageNames,
} from '@wit/vtv-x-foundation-lib';
import { NavigationPathSections } from './navigationPathWatcher';

// Keep this path sections synchronized with the paths.ts file
export enum MAIN_PATH_SECTIONS {
  MY_TV = 'mytv',
  EPG = 'watchtv',
  ON_DEMAND = 'ondemand',
  SETTINGS = 'settings',
  SEARCH = 'search',
  SIGNIN = 'signin',
  ERROR = 'error',
  PROFILES = 'profiles',
  GDPR = 'gdpr',
  ABOUT = 'about',
  PRIVACY = 'privacy',
  HELP = 'help',
}

// order matters
export enum SUB_PATH_SECTIONS {
  SEE_MORE = 'all',
  RELATED = 'related',
  FOLDER = 'folder',
  PROGRAM_INFO = 'info',
  RECORDINGS = 'recordings',
  PARENTAL_CONTROL = 'parental',
  PROFILES = 'profiles',
  PIN = 'pin',
  PRIVACY = 'privacy',
}

export const SUB_SECTIONS = Object.values(SUB_PATH_SECTIONS);
export const MAIN_SECTIONS = Object.values(MAIN_PATH_SECTIONS);

const subPageNameMap: { [key: string]: PageToPageNavigationPageNames } = {
  [SUB_PATH_SECTIONS.PROGRAM_INFO]: PageToPageNavigationPageNames.PROGRAM_INFO,
  [SUB_PATH_SECTIONS.RECORDINGS]: PageToPageNavigationPageNames.RECORDINGS,
  [SUB_PATH_SECTIONS.RELATED]: PageToPageNavigationPageNames.RELATED,
  [SUB_PATH_SECTIONS.SEE_MORE]: PageToPageNavigationPageNames.SEE_MORE,
  [SUB_PATH_SECTIONS.FOLDER]: PageToPageNavigationPageNames.ON_DEMAND,
};

const mainPageNameMap: { [key: string]: PageToPageNavigationPageNames } = {
  [MAIN_PATH_SECTIONS.ABOUT]: PageToPageNavigationPageNames.SETTINGS,
  [MAIN_PATH_SECTIONS.EPG]: PageToPageNavigationPageNames.EPG,
  [MAIN_PATH_SECTIONS.ERROR]: PageToPageNavigationPageNames.SETTINGS, // TODO: improve error main page detection
  [MAIN_PATH_SECTIONS.GDPR]: PageToPageNavigationPageNames.SETTINGS,
  [MAIN_PATH_SECTIONS.HELP]: PageToPageNavigationPageNames.SETTINGS,
  [MAIN_PATH_SECTIONS.MY_TV]: PageToPageNavigationPageNames.MY_TV,
  [MAIN_PATH_SECTIONS.ON_DEMAND]: PageToPageNavigationPageNames.ON_DEMAND,
  [MAIN_PATH_SECTIONS.PRIVACY]: PageToPageNavigationPageNames.SETTINGS,
  [MAIN_PATH_SECTIONS.PROFILES]: PageToPageNavigationPageNames.SIGN_IN,
  [MAIN_PATH_SECTIONS.SEARCH]: PageToPageNavigationPageNames.SEARCH,
  [MAIN_PATH_SECTIONS.SETTINGS]: PageToPageNavigationPageNames.SETTINGS,
  [MAIN_PATH_SECTIONS.SIGNIN]: PageToPageNavigationPageNames.SIGN_IN,
};

export function getMainPageName(path: string): PageToPageNavigationPageNames | undefined {
  for (let j = 0; j < MAIN_SECTIONS.length; j += 1) {
    if (path.startsWith(`/${MAIN_SECTIONS[j]}`)) {
      return mainPageNameMap[MAIN_SECTIONS[j]];
    }
  }
  return undefined;
}

export function getSubPageName(path: string): PageToPageNavigationPageNames | undefined {
  for (let i = 0; i < SUB_SECTIONS.length; i += 1) {
    if (path.indexOf(`/${SUB_SECTIONS[i]}`) >= 0) {
      return subPageNameMap[SUB_SECTIONS[i]];
    }
  }
  return undefined;
}

const subPathMap: { [key: string]: ActionsEventNavigationPathSegment } = {
  [SUB_PATH_SECTIONS.PROGRAM_INFO]: ActionsEventNavigationPathSegment.PI,
  [SUB_PATH_SECTIONS.RELATED]: ActionsEventNavigationPathSegment.PI,
  [SUB_PATH_SECTIONS.RECORDINGS]: ActionsEventNavigationPathSegment.RECORDINGS,
  [SUB_PATH_SECTIONS.FOLDER]: ActionsEventNavigationPathSegment.FOLDER,
  [SUB_PATH_SECTIONS.PARENTAL_CONTROL]: ActionsEventNavigationPathSegment.PARENTAL_CONTROL,
  [SUB_PATH_SECTIONS.PROFILES]: ActionsEventNavigationPathSegment.PROFILE,
  [SUB_PATH_SECTIONS.PIN]: ActionsEventNavigationPathSegment.PIN,
  [SUB_PATH_SECTIONS.PRIVACY]: ActionsEventNavigationPathSegment.TERMS_CONDITIONS,
};

const mainPathMap: { [key: string]: ActionsEventNavigationPathSegment | null } = {
  [MAIN_PATH_SECTIONS.ABOUT]: ActionsEventNavigationPathSegment.SETTINGS,
  [MAIN_PATH_SECTIONS.EPG]: ActionsEventNavigationPathSegment.EPG,
  [MAIN_PATH_SECTIONS.ERROR]: null,
  [MAIN_PATH_SECTIONS.GDPR]: ActionsEventNavigationPathSegment.SETTINGS,
  [MAIN_PATH_SECTIONS.HELP]: ActionsEventNavigationPathSegment.SETTINGS,
  [MAIN_PATH_SECTIONS.MY_TV]: ActionsEventNavigationPathSegment.MY_TV,
  [MAIN_PATH_SECTIONS.ON_DEMAND]: ActionsEventNavigationPathSegment.ON_DEMAND,
  [MAIN_PATH_SECTIONS.PRIVACY]: ActionsEventNavigationPathSegment.SETTINGS,
  [MAIN_PATH_SECTIONS.PROFILES]: ActionsEventNavigationPathSegment.PROFILE,
  [MAIN_PATH_SECTIONS.SEARCH]: ActionsEventNavigationPathSegment.SEARCH,
  [MAIN_PATH_SECTIONS.SETTINGS]: ActionsEventNavigationPathSegment.SETTINGS,
  [MAIN_PATH_SECTIONS.SIGNIN]: null,
};

export function getMainPathName(path: string): ActionsEventNavigationPathSegment | null | undefined {
  for (let j = 0; j < MAIN_SECTIONS.length; j += 1) {
    if (path.startsWith(`/${MAIN_SECTIONS[j]}`)) {
      return mainPathMap[MAIN_SECTIONS[j]];
    }
  }
  return undefined;
}

export function getSubPathName(path: string): ActionsEventNavigationPathSegment | undefined {
  for (let i = 0; i < SUB_SECTIONS.length; i += 1) {
    if (path.indexOf(`/${SUB_SECTIONS[i]}`) > 0) {
      return subPathMap[SUB_SECTIONS[i]];
    }
  }
  return undefined;
}

function getSimplifiedActionName(actionName: ActionsEventActionNames): string {
  // Workaround for differences between the real action name and the action name used in navigation paths in spec TF25.001.01
  return actionName.replace(' Profile', '').replace(' Parental Control', '');
}

export function sectionsToNavigationPath(sections: NavigationPathSections[], actionName?: ActionsEventActionNames) {
  const navigationPath = sections.reduce((acc, val) => {
    let newVal = `${acc}/${val.path}`;
    if (val.additionalInfo) {
      newVal = `${newVal}/${val.additionalInfo}`;
    }
    return newVal;
  }, '');
  if (actionName) {
    return `${navigationPath}/${getSimplifiedActionName(actionName)}`;
  }
  return navigationPath;
}
